import { Card } from "@design";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useProposalContext } from "../research/ProposalContext";
import { ActionChoice } from "./ActionChoice";
import { ActionOutcome, ActionStatusBadge } from "./ActionOutcome";
import { ActionSummary } from "./ActionSummary";
import type { ActionProposal } from "./actionModel";

/**
 * A write action the assistant wants to take (MHS-971): what it is, where it goes and the exact values it will send.
 * Nothing happens until the chat's owner approves; approving runs exactly what the card shows (with their edits).
 * Decided and replaced actions, and everyone else's view of the chat, are read-only; a finished one shows its receipt.
 */
export function ActionApprovalCard({ action }: Readonly<{ action: ActionProposal }>) {
  const { t } = useTranslation("chat", { keyPrefix: "action" });
  const { readOnly } = useProposalContext();
  const [editing, setEditing] = useState(false);
  const decidable = action.status === "pending" && !readOnly;
  const decided = action.status === "confirmed" || action.status === "declined";
  return (
    <Card>
      <section aria-label={t("label")} className="space-y-3">
        <ActionSummary
          action={action}
          omit={decidable && editing ? Object.keys(action.editable) : []}
          status={decided ? <ActionStatusBadge action={action} /> : undefined}
        />
        {decidable ? (
          <ActionChoice action={action} editing={editing} onEditing={setEditing} />
        ) : (
          <ActionOutcome action={action} />
        )}
      </section>
    </Card>
  );
}
